import { find, includes, map } from "lodash";
import React, {
  useState,
  useContext,
  createContext,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel } from "@/shared/constants";
import { ResourceEnum } from "@/shared/constants/resources";
import { IconName } from "@/shared/ui/Icon";
import { useResponsive } from "@/shared/utils/hooks/useResponsive";

export interface SidebarLink {
  key: string;
  icon: IconName;
  label: string;
  url?: string;
  isActive?: boolean;
  isHidden?: boolean;
}

export const NavigationContext = createContext<NavigationContextType>({
  toggleMobileSidebar: () => {},
  closeMobileSidebar: () => {},
  sidebarLinks: [],
  dashboardLink: {} as SidebarLink,
  mobileOpen: false,
  shouldUseMobileUI: false,
});

interface NavigationContextType {
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  sidebarLinks: SidebarLink[];
  dashboardLink: SidebarLink;
  mobileOpen: boolean;
  shouldUseMobileUI: boolean;
}

interface NavigationProviderProps {
  children: React.ReactNode;
}

const defaultFeaturesConfig = {
  people: true,
  teams: true,
  hiring: true,
  scorecards: true,
  positions: true,
  projects: true,
};

function NavigationProvider(props: NavigationProviderProps) {
  const { children } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const { shouldUseMobileUI } = useResponsive();

  // Close mobile sidebar when switching to desktop mode
  useEffect(() => {
    if (!shouldUseMobileUI) {
      setMobileOpen(false);
    }
  }, [shouldUseMobileUI]);

  const { account } = useAccount();
  const { organization: selectedOrg } = useOrganisation();
  const { validateAccessToResource } = useViewerPermissions();

  // The scorecard library is admin/owner only (account EDIT/FULL); hiding the
  // entry is UX only — the route guard and the API are the real gates
  const canManageScorecards = validateAccessToResource({
    resourceType: ResourceEnum.account,
    accessLevel: AccessLevel.edit,
  });

  const { pathname } = useLocation();

  const handleSetMobileOpen = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleCloseMobileSidebar = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const featuresConfig = {
    ...defaultFeaturesConfig,
    ...(account.config?.features || {}),
  };

  const sidebarLinks = useMemo(
    () =>
      map(
        [
          {
            key: "people",
            id: "sidebar-link-people",
            icon: "User" as IconName,
            label: t("feature.people"),
            url: `/org/${selectedOrg.id}/members`,
            isActive:
              includes(pathname, "members") && !includes(pathname, "account"),
          },
          {
            key: "teams",
            id: "sidebar-link-teams",
            icon: "Users" as IconName,
            label: t("feature.teams"),
            url: `/org/${selectedOrg.id}/teams`,
            isActive: includes(pathname, "teams"),
          },
          {
            key: "projects",
            id: "sidebar-link-projects",
            icon: "Projects" as IconName,
            label: t("feature.projects"),
            url: `/org/${selectedOrg.id}/projects`,
            isActive: includes(pathname, "project"),
          },
          {
            key: "scorecards",
            id: "sidebar-link-scorecards",
            icon: "Clipboard" as IconName,
            label: t("feature.scorecards"),
            url: `/org/${selectedOrg.id}/scorecards`,
            isActive: includes(pathname, "scorecards"),
          },
        ],
        (link) => ({
          ...link,
          isHidden:
            !featuresConfig[link.key] ||
            account?.config?.landing_route === link.key ||
            (link.key === "scorecards" && !canManageScorecards),
        })
      ),
    [selectedOrg, account, pathname, t, featuresConfig, canManageScorecards]
  );

  const dashboardLink = useMemo(() => {
    const landingRoute = account.config?.landing_route;
    const landingRouteLink = find(sidebarLinks, { key: landingRoute });
    if (landingRouteLink) {
      return {
        ...landingRouteLink,
        isHidden: false,
      };
    }
    return {
      key: "dashboard",
      id: "sidebar-link-dashboard",
      icon: "House" as IconName,
      label: t("feature.dashboard"),
      url: `/org/${selectedOrg.id}/dashboard`,
    };
  }, [selectedOrg, t, account.config?.landing_route, sidebarLinks]);

  const contextValue = useMemo(
    () => ({
      sidebarLinks,
      dashboardLink,
      mobileOpen,
      shouldUseMobileUI,
      toggleMobileSidebar: handleSetMobileOpen,
      closeMobileSidebar: handleCloseMobileSidebar,
    }),
    [
      sidebarLinks,
      mobileOpen,
      shouldUseMobileUI,
      dashboardLink,
      handleSetMobileOpen,
      handleCloseMobileSidebar,
    ]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
}

export const useNavigationContainer = () => useContext(NavigationContext);

export default NavigationProvider;
